import React from "react";

const LS_KEY_PALETTE   = "promise_palette_mode";
const LS_KEY_DARK_PREF = "promise_dark_pref";  // "auto" | "light" | "dark"
const LS_KEY_DARK_OLD  = "promise_dark_mode";  // ancienne clé booléenne (migration)

/**
 * Catalogue des palettes d'accessibilité.
 * `colors: null` → on utilise la couleur de l'application (config.color).
 * `swatches` → 4 couleurs représentatives affichées dans le sélecteur.
 */
export const PALETTES = {
    default: {
        id: "default",
        label: "Normal",
        description: "Couleurs de l'application par défaut",
        colors: null,
        swatches: ["#1976d2", "#9c27b0", "#2e7d32", "#ed6c02"],
    },
    deuteranopia: {
        id: "deuteranopia",
        label: "Deutéranopie / Protanopie",
        description: "Daltonisme rouge-vert (~8 % des hommes) · Palette Okabe-Ito",
        colors: {
            primary:   { main: "#0072B2" },
            secondary: { main: "#E69F00" },
            error:     { main: "#CC79A7" },
            warning:   { main: "#D55E00" },
            success:   { main: "#009E73" },
            info:      { main: "#56B4E9" },
        },
        swatches: ["#0072B2", "#E69F00", "#009E73", "#D55E00"],
    },
    tritanopia: {
        id: "tritanopia",
        label: "Tritanopie",
        description: "Daltonisme bleu-jaune · Palette rouge / vert",
        colors: {
            primary:   { main: "#B22222" },
            secondary: { main: "#228B22" },
            error:     { main: "#8B0000" },
            warning:   { main: "#FF6347" },
            success:   { main: "#006400" },
            info:      { main: "#C71585" },
        },
        swatches: ["#B22222", "#228B22", "#C71585", "#FF6347"],
    },
    highContrast: {
        id: "highContrast",
        label: "Contraste élevé",
        description: "Contraste maximum pour malvoyants",
        colors: {
            primary:   { main: "#000000" },
            secondary: { main: "#1A237E" },
            error:     { main: "#B71C1C" },
            warning:   { main: "#E65100" },
            success:   { main: "#1B5E20" },
            info:      { main: "#01579B" },
        },
        swatches: ["#000000", "#1A237E", "#B71C1C", "#1B5E20"],
    },
};

export const ThemeContext = React.createContext(null);

/** Lit la préférence de couleur du système (OS). */
function getSystemDark() {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/**
 * Fournit paletteMode + darkModePreference ("auto"|"light"|"dark") à toute
 * l'arborescence. En mode "auto", le thème suit la préférence de l'OS.
 * Persiste les préférences dans localStorage.
 */
export function ThemeToggleProvider({ children }) {
    const [paletteMode, setPaletteModeState] = React.useState(() => {
        const saved = localStorage.getItem(LS_KEY_PALETTE);
        return saved && PALETTES[saved] ? saved : "default";
    });

    /** "auto" | "light" | "dark" — avec migration depuis l'ancienne clé booléenne */
    const [darkModePreference, setDarkModePrefState] = React.useState(() => {
        const saved = localStorage.getItem(LS_KEY_DARK_PREF);
        if (saved === "auto" || saved === "light" || saved === "dark") return saved;
        // Migration depuis l'ancienne clé booléenne
        const old = localStorage.getItem(LS_KEY_DARK_OLD);
        if (old === "true")  return "dark";
        if (old === "false") return "light";
        return "auto"; // par défaut : suivre le système
    });

    const [systemDark, setSystemDark] = React.useState(getSystemDark);

    /** Écoute en temps réel les changements de thème de l'OS */
    React.useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e) => setSystemDark(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    /** Valeur effective du mode sombre */
    const darkMode =
        darkModePreference === "dark"  ? true  :
        darkModePreference === "light" ? false :
        systemDark;

    function setPaletteMode(id) {
        if (!PALETTES[id]) return;
        localStorage.setItem(LS_KEY_PALETTE, id);
        setPaletteModeState(id);
    }

    function setDarkModePreference(pref) {
        if (!["auto", "light", "dark"].includes(pref)) return;
        localStorage.setItem(LS_KEY_DARK_PREF, pref);
        setDarkModePrefState(pref);
    }

    /** Compatibilité avec les usages existants de toggleDarkMode */
    function toggleDarkMode() {
        setDarkModePreference(darkMode ? "light" : "dark");
    }

    const value = React.useMemo(
        () => ({
            paletteMode, setPaletteMode,
            PALETTES,
            darkMode, darkModePreference, setDarkModePreference,
            toggleDarkMode,
        }),
        [paletteMode, darkMode, darkModePreference]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Hook utilitaire pour consommer ThemeContext */
export function useThemeContext() {
    const ctx = React.useContext(ThemeContext);
    if (!ctx) throw new Error("useThemeContext doit être utilisé dans ThemeToggleProvider");
    return ctx;
}
